import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AddUserForm, useResendInvitation } from '@/features/auth';
import {
  can,
  useIdentity,
  DELETE_USER_PERMISSION,
  DISABLE_USER_PERMISSION,
  ADMIN_PERMISSION,
} from '@/entities/session';

import { SCREEN_PATHS } from '@/shared/routes';
import { Button } from '@/shared/ui/Button';
import { DataTable, PrimaryCell } from '@/shared/ui/DataTable';
import { Section, Sections } from '@/shared/ui/Layout';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { UnavailableState } from '@/shared/ui/States';

import {
  useDeleteUser,
  useRoles,
  useSetRoles,
  useSetStatus,
  useTenantUsers,
} from '../api/access';
import {
  canDisable,
  lastSeen,
  orderRoles,
  mayResendInvitation,
  orderUsers,
  starterLabelKey,
  statusLabelKey,
  statusTone,
} from '../model/access';
import { RoleMatrix } from './RoleMatrix';
import { UserDrawer } from './UserDrawer';

import type { TenantUser } from '@/shared/api/generated/tenancy';

/* Who can sign in to this tenant, and what their role lets them do.
 *
 * **Everything on this screen is backed by a real route.** The six under `/v1/auth` — `me`, the
 * user listing, role replacement, enable/disable, the role list and the permission catalogue — were
 * built for exactly this surface. The reference mockup marks the users and roles tables
 * `BACKEND SUPPORT REQUIRED`; they no longer are, so the badge is gone rather than kept as
 * decoration. What genuinely has no route is **authoring a role**, and that is where the marker
 * sits now.
 *
 * **The propagation notice is pinned above the tables and is not reassurance copy.**
 * `DecisionCache` invalidates when a role is *unbound* from a person, not when a role's *contents*
 * change — so a role assignment takes effect at once and an edit to what a role grants does not.
 * The two halves are stated separately because they behave differently, and a reader who assumed
 * the second from the first would be wrong in the dangerous direction.
 *
 * **Configuration is link-chips, not tabs.** Seven of the eight things an administrator does here
 * already have screens; duplicating them as tabs would give one screen two routes, and the reference
 * spec resolves this the same way. Users & access carries the page, so the landing never feels
 * empty.
 */

export function AdminScreen(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const users = useTenantUsers();
  const roles = useRoles();
  const setRoles = useSetRoles();
  const setStatus = useSetStatus();
  const deleteUser = useDeleteUser();

  const [addOpen, setAddOpen] = useState(false);
  const [managing, setManaging] = useState<TenantUser | null>(null);
  const resend = useResendInvitation();
  const [matrixRole, setMatrixRole] = useState<string | null>(null);
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  /* Read once per mount rather than per render, so the day boundary in `lastSeen` is stable while a
   * reader is looking at the table. `HomeScreen`'s `now` is the precedent and the reason is the same:
   * a component that calls `new Date()` inline re-renders with a different answer on every unrelated
   * state change, and a "Today" that becomes "Yesterday" mid-session is a table that moves. */
  const [now] = useState(() => new Date());

  const callerSubject = identity.data?.subject ?? '';
  const mayDisable = can(identity.data, DISABLE_USER_PERMISSION);
  const mayGrant = can(identity.data, ADMIN_PERMISSION);
  /* **Not narrowed by `canDisable`'s self check, unlike `mayDisable` above.** The server refuses a
   * self-delete and a last-admin delete with a 422 naming which, and both are worth reading — see
   * `UserDrawer.mayDelete`. The permission alone is the gate, and it hides a control rather than
   * protecting anything: the route refuses regardless of what this browser believes. */
  const mayDelete = can(identity.data, DELETE_USER_PERMISSION);

  const allUsers = users.isSuccess ? orderUsers(users.data.items) : [];
  const term = search.trim().toLowerCase();
  const visible = allUsers.filter(
    (user) =>
      (includeDisabled || !user.disabled) &&
      (roleFilter === '' || user.roles.includes(roleFilter)) &&
      /* Email or display name. An administrator hunting for somebody types whichever they remember,
         and matching one of the two would make the box look broken half the time. */
      (term === '' ||
        user.subject.toLowerCase().includes(term) ||
        user.display_name.toLowerCase().includes(term)),
  );
  const roleList = roles.isSuccess ? orderRoles(roles.data.items) : [];

  return (
    <Sections>
      <PageHeader
        title={t('admin.title')}
        lede={t('admin.lede')}
        actions={
          mayGrant ? (
            /* **A plain button, not a toggle.** It used to flip its own label to Cancel and carry
               `aria-expanded`, which is the right pair for a panel that pushes the page down — the
               control both opens the region and is where a reader looks to close it again. A modal
               is dismissed from inside itself, by its own close control, Escape or the backdrop, so
               a second Cancel out here would be a fourth path to the same thing and
               `aria-expanded` would describe a disclosure that no longer exists. */
            <Button
              tone="primary"
              onClick={() => {
                setAddOpen(true);
              }}
            >
              {t('admin.addUser')}
            </Button>
          ) : undefined
        }
      />

      {/* **The row includes the page it is on**, marked current rather than omitted. A pill row of
          seven links with the eighth missing reads as navigation *away* from somewhere unnamed; with
          the current one in it, it reads as a set a reader is inside. `aria-current="page"` is what
          says so to a screen reader, and it is a `span` rather than a `Link` because a link to here
          is a dead control. */}
      <nav className="lq-chips" aria-label={t('admin.configuration')}>
        <span className="lq-chip lq-chip--current" aria-current="page">
          {t('admin.link.usersAndAccess')}
        </span>
        {CONFIG_LINKS.map((link) => (
          <Link key={link.path} to={link.path} className="lq-chip">
            {t(link.labelKey)}
            <span aria-hidden="true"> ↗</span>
          </Link>
        ))}
      </nav>

      {/* A strip rather than an `Alert`, and the distinction is what it *is*: an `Alert` has a
          heading and reads as something that happened, and this is a standing fact about how the
          platform works. A heading over a permanent fact turns it into a notice a reader learns to
          skip past — on the one sentence that explains why a revoked role still worked a minute
          ago. */}
      <p className="lq-strip lq-strip--warn">
        <strong className="lq-strip__lead">{t('admin.propagation.title')}</strong>
        <span className="lq-strip__body">{t('admin.propagation.body')}</span>
      </p>

      {/* ---------------------------------------------------------------- users */}
      <Section title={t('admin.users.title')} hint={t('admin.users.hint')}>
        <div className="lq-toolbar">
          {/* **Client-side, and that is right here where it is wrong on the catalogue.** `useDatasets`
              pushes its search into SQL because an estate has thousands of tables and filtering a
              fetched page would silently search only the first one. A tenant's user list is bounded
              by how many people work at the customer, `GET /v1/auth/users` returns all of them, and
              filtering in the browser is instant with no round trip. Matched against the email *and*
              the display name, because an administrator looking for somebody types whichever they
              remember. */}
          <label className="lq-field lq-field--inline">
            <span className="lq-field__label">{t('admin.filter.search')}</span>
            <input
              className="lq-input"
              type="search"
              value={search}
              placeholder={t('admin.filter.searchPlaceholder')}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </label>
          <label className="lq-field lq-field--inline">
            <span className="lq-field__label">{t('admin.filter.role')}</span>
            <select
              className="lq-input"
              value={roleFilter}
              onChange={(event) => {
                setRoleFilter(event.target.value);
              }}
            >
              <option value="">{t('admin.filter.allRoles')}</option>
              {roleList.map((role) => (
                <option key={role.name} value={role.name}>
                  {role.name}
                </option>
              ))}
            </select>
          </label>
          <label className="lq-checkbox">
            <input
              className="lq-checkbox__input"
              type="checkbox"
              checked={includeDisabled}
              onChange={(event) => {
                setIncludeDisabled(event.target.checked);
              }}
            />
            <span className="lq-checkbox__label">
              {t('admin.filter.includeDisabled')}
            </span>
          </label>
          <span className="lq-toolbar__spacer" />
          <span className="lq-toolbar__count">
            {t('admin.users.count', { shown: visible.length, total: allUsers.length })}
          </span>
        </div>

        {users.isPending ? (
          <SkeletonRows rows={4} />
        ) : users.isError ? (
          /* **An empty user list is impossible**, because the signup admin always exists — so a
             read that returns nothing is a failure, and saying "no users" would be a false claim
             about the tenant rather than a report about the request. */
          <UnavailableState
            title={t('admin.users.unreadable')}
            guidance={users.error.message}
          />
        ) : (
          <DataTable
            caption={t('admin.users.caption')}
            columns={[
              { key: 'user', label: t('admin.column.user') },
              { key: 'roles', label: t('admin.column.roles') },
              { key: 'lastSeen', label: t('admin.column.lastSeen') },
              { key: 'status', label: t('admin.column.status') },
              { key: 'actions', label: t('admin.column.actions'), actions: true },
            ]}
          >
            {visible.map((user) => (
              <tr key={user.subject}>
                <td>
                  <PrimaryCell secondary={user.subject}>{user.display_name}</PrimaryCell>
                </td>
                <td>
                  {user.roles.length === 0 ? (
                    /* Not a dash. An empty role set denies everything, which is a state somebody
                       has to act on - and it is the normal state of a teammate just invited. */
                    <span className="lq-muted">{t('admin.noRoles')}</span>
                  ) : (
                    /* Pills rather than a comma-joined string, and only the privileged one is
                       tinted. See `.lq-role` for why the other three are deliberately alike: a
                       reader scanning this column is asking who is an admin, and ranking the rest
                       in ink would imply a seniority the permission catalogue does not have. */
                    <span className="lq-roles">
                      {user.roles.map((role) => (
                        <span
                          key={role}
                          className={
                            role === PRIVILEGED_ROLE
                              ? 'lq-role lq-role--privileged'
                              : 'lq-role'
                          }
                        >
                          {role}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
                <td>
                  <LastSeenCell lastLoginAt={user.last_login_at} now={now} />
                </td>
                <td>
                  <StatusBadge
                    tone={statusTone(user)}
                    label={t(statusLabelKey(user))}
                    {...(user.disabled
                      ? { title: t('admin.status.disabledMeaning') }
                      : mayResendInvitation(user)
                        ? { title: t('admin.status.pendingMeaning') }
                        : {})}
                  />
                </td>
                <td className="lq-table__actions">
                  {/* **Offered only for an invited account.** The server answers 409 for one that
                      already has a password, because re-inviting it could only mean clearing it —
                      so showing the button everywhere would put a refusal behind a control that
                      looks available. See `mayResendInvitation`. */}
                  {mayGrant && mayResendInvitation(user) && (
                    <Button
                      tone="secondary"
                      busy={resend.isPending && resend.variables === user.subject}
                      onClick={() => {
                        resend.mutate(user.subject);
                      }}
                    >
                      {t('admin.resendInvitation')}
                    </Button>
                  )}
                  {mayGrant && (
                    <Button
                      tone="secondary"
                      onClick={() => {
                        setManaging(user);
                      }}
                    >
                      {t('admin.manage')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </Section>

      {/* ---------------------------------------------------------------- roles */}
      <Section title={t('admin.roles.title')} hint={t('admin.roles.hint')}>
        {roles.isPending ? (
          <SkeletonRows rows={4} />
        ) : roles.isError ? (
          <UnavailableState
            title={t('admin.roles.unreadable')}
            guidance={roles.error.message}
          />
        ) : (
          <>
            <DataTable
              caption={t('admin.roles.caption')}
              columns={[
                { key: 'role', label: t('admin.column.role') },
                { key: 'meantFor', label: t('admin.column.meantFor') },
                {
                  key: 'permissions',
                  label: t('admin.column.permissions'),
                  numeric: true,
                },
                { key: 'holders', label: t('admin.column.holders'), numeric: true },
                { key: 'actions', label: t('admin.column.actions'), actions: true },
              ]}
            >
              {roleList.map((role) => (
                <tr key={role.name}>
                  <td>
                    <PrimaryCell secondary={t(starterLabelKey(role))}>
                      {role.name}
                    </PrimaryCell>
                  </td>
                  <td>{role.description}</td>
                  <td className="lq-table__num">{role.permissions.length}</td>
                  <td className="lq-table__num">{role.holders}</td>
                  <td className="lq-table__actions">
                    <Button
                      tone="secondary"
                      onClick={() => {
                        setMatrixRole(role.name);
                      }}
                    >
                      {t('admin.viewMatrix')}
                    </Button>
                  </td>
                </tr>
              ))}
            </DataTable>
            <p className="lq-strip lq-strip--info">
              <strong className="lq-strip__lead">{t('admin.roles.twoEyesLead')}</strong>
              <span className="lq-strip__body">{t('admin.roles.twoEyes')}</span>
            </p>
            {/* Why the table has no edit control, kept as a plain note rather than a second strip:
                two tinted strips in a row read as two warnings, and only the first is one. */}
            <p className="lq-form-note">{t('admin.roles.noAuthoring')}</p>
          </>
        )}
      </Section>

      {/* **Over the page rather than in it**, and grouped with the other two overlays rather than
          left where the inline panel used to sit. As a panel it pushed both tables down by its own
          height on open and pulled them back on close, so the row an administrator was reading moved
          twice for an action that has nothing to do with it — and on a long user list it could open
          below the fold, which makes the button look broken.
          The title and hint are the form's own copy keys, rendered by the dialog's head rather than
          by a card inside it; see `AddUserForm`. */}
      <Modal
        open={addOpen}
        title={t('auth.team.title')}
        hint={t('auth.team.hint')}
        onClose={() => {
          setAddOpen(false);
        }}
      >
        <AddUserForm
          /* **Deliberately does not close the dialog.** It used to, and that threw away the one
             thing the administrator needs: `welcome_email` says whether the password was actually
             delivered, and with no mail transport — this deployment's current state — nobody has
             told the new person anything and only the administrator can. The form renders that
             notice, so closing on success unmounted the message before it could be read. The
             listing behind refreshes either way; the dialog closes when its reader is done with
             it. */
          onAdded={() => {
            void users.refetch();
          }}
          onCancel={() => {
            setAddOpen(false);
          }}
        />
      </Modal>

      {managing !== null && (
        <UserDrawer
          user={managing}
          roles={roleList}
          callerSubject={callerSubject}
          mayDisable={mayDisable && canDisable(managing, callerSubject)}
          mayDelete={mayDelete}
          onClose={() => {
            setManaging(null);
            /* A refusal must not survive the drawer. Without this, reopening the drawer on a
               different person would show the previous delete's reason — the same stale-error class
               `ConfirmDialog` avoids by unmounting while closed. */
            deleteUser.reset();
          }}
          onSaveRoles={(roleNames) => {
            setRoles.mutate(
              { subject: managing.subject, roleNames },
              {
                onSuccess: () => {
                  setManaging(null);
                },
              },
            );
          }}
          onSetDisabled={(disabled) => {
            setStatus.mutate(
              { subject: managing.subject, disabled },
              {
                onSuccess: () => {
                  setManaging(null);
                },
              },
            );
          }}
          onDelete={() => {
            deleteUser.mutate(managing.subject, {
              onSuccess: () => {
                /* Closing the drawer is what dismisses the confirmation — it unmounts with its
                   parent. On a refusal the drawer stays open and the dialog keeps the reason, which
                   is the behaviour `ConfirmDialog`'s `error` prop exists for. */
                setManaging(null);
              },
            });
          }}
          saving={setRoles.isPending || setStatus.isPending}
          error={setRoles.error ?? setStatus.error}
          deleting={deleteUser.isPending}
          deleteError={deleteUser.error}
        />
      )}

      {matrixRole !== null && (
        <RoleMatrix
          roleName={matrixRole}
          roles={roleList}
          onClose={() => {
            setMatrixRole(null);
          }}
        />
      )}
    </Sections>
  );
}

/* The seven existing administration screens, reached from here rather than duplicated here. */
/* When somebody last signed in, in three shapes rather than one.
 *
 * See `lastSeen` for why the precision falls off with age. The clock time is formatted here rather
 * than in the message, matching every other instant on this platform: `i18next-icu` cannot render
 * ICU's `time` type — §9 records finding that by bisection — so a message spelling a clock time
 * comes back as its own source.
 */
function LastSeenCell({
  lastLoginAt,
  now,
}: {
  readonly lastLoginAt: string | null;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  const seen = lastSeen(lastLoginAt, now);

  if (seen.kind === 'never') {
    /* Never signed in is a different fact from a dormant account, and it is the one an administrator
       chasing an onboarding acts on — so it is words, never a dash and never an epoch. */
    return <span className="lq-muted">{t('admin.neverSignedIn')}</span>;
  }
  if (seen.kind === 'today') {
    return (
      <span title={seen.at.toLocaleString()}>
        {t('admin.lastSeen.today', { clock: clockTime(seen.at) })}
      </span>
    );
  }
  if (seen.kind === 'yesterday') {
    return (
      <span title={seen.at.toLocaleString()}>
        {t('admin.lastSeen.yesterday', { clock: clockTime(seen.at) })}
      </span>
    );
  }
  return (
    <span title={seen.at.toLocaleString()}>
      {t('admin.lastSeen.earlier', { at: seen.at })}
    </span>
  );
}

/** `08:14`. Formatted in the component for the reason above. */
function clockTime(at: Date): string {
  return at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/* The one role whose pill is tinted. Not derived from a permission count — `admin` is the role that
 * can grant roles, which is the property an access review is looking for, and a count is a proxy that
 * would move the highlight the day somebody widens `data-steward`. */
const PRIVILEGED_ROLE = 'admin';

const CONFIG_LINKS: readonly { readonly path: string; readonly labelKey: string }[] = [
  { path: SCREEN_PATHS.connections, labelKey: 'admin.link.connectors' },
  { path: SCREEN_PATHS.serving, labelKey: 'admin.link.modelRouting' },
  { path: SCREEN_PATHS.alertRouting, labelKey: 'admin.link.alertRouting' },
  { path: SCREEN_PATHS.metering, labelKey: 'admin.link.quotas' },
  { path: SCREEN_PATHS.scoreModel, labelKey: 'admin.link.scoreModel' },
  { path: SCREEN_PATHS.policy, labelKey: 'admin.link.accessPolicy' },
  { path: SCREEN_PATHS.platform, labelKey: 'admin.link.deployment' },
];
